import { Controller } from "@hotwired/stimulus"

// Preloads the cold search modal into a turbo-permanent container in the layout,
// so tapping the header search icon on a non-results page opens the modal
// instantly instead of waiting on a full navigation to /videos. The fetch runs
// once, just after the page settles, and the permanent container carries the
// loaded modal across subsequent Turbo visits. Until it lands, the header icon
// falls back to navigating (open_modal_filters_controller).
export default class extends Controller {
  static values = { url: String }

  connect() {
    // A permanent element carried across a Turbo visit is already populated.
    if (this.element.dataset.loaded) return;
    // Defer past first paint so the prefetch never competes with the page render.
    this.timer = setTimeout(() => this.load(), 0);
  }

  disconnect() {
    if (this.timer) clearTimeout(this.timer);
  }

  async load() {
    if (this.element.dataset.loaded || this.loading) return;
    this.loading = true;
    try {
      const response = await fetch(this.urlValue, {
        headers: { Accept: "text/html" },
        credentials: "same-origin",
      });
      // A guarded endpoint can redirect this fragment request to a full page
      // (for example, an incomplete profile is redirected back to onboarding).
      // Injecting that document would mount another search-modal-loader inside
      // this one and recursively duplicate the page forever.
      if (!response.ok || response.redirected) return;

      const html = await response.text();
      // Defence in depth: only inject the cold-modal fragment this endpoint is
      // contracted to return. A 200 error/login/application page is not safe to
      // put inside the permanent modal container either.
      if (!html.includes("data-cold-modal")) return;

      this.element.innerHTML = html;
      this.element.dataset.loaded = "true";
    } catch (_error) {
      // Leave it unloaded; the icon still works via navigation fallback.
    } finally {
      this.loading = false;
    }
  }
}
